/**
 * COPY STRATEGY — CommissionVatPage
 *
 * Awareness:      Problem-Aware (Stage 2/5)
 *                 Many drivers don't even know reverse VAT exists.
 *                 Those who do often forget or don't know how to calculate it.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 Explains the reverse VAT mechanism: why YOU pay VAT on Bolt's
 *                 commission (EU cross-border service), how to calculate, declare,
 *                 and even reclaim money at year-end.
 *
 * Avatar:         Any active Bolt/Uber driver who hasn't been declaring
 *                 reverse VAT. Likely 3–12 months in, just learned about it
 *                 or got a VMI warning letter.
 *
 * Hook:           Fear — "mokestis, kurį dauguma pavežėjų pamiršta" + VMI fines
 * Structure:      What is it → Why you must pay → How to calculate → How to declare → CTA
 * Proof:          Concrete calculation example with Bolt commission SF
 * Funnel:         Pairs with DAC7 page, leads to Finhudas auto-calculation
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiWarning } from 'react-icons/pi'
import { Link } from 'react-router-dom'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const CommissionVatPage = () => {
  usePageSetup('Atvirkštinis PVM: mokestis, kurį dauguma pavežėjų pamiršta', {
    scroll: true,
    description: 'Kas yra atvirkštinis PVM ir kodėl VMI baudžia pavežėjus, kurie jo nedeklaruoja? Sužinokite, kaip apskaičiuoti 21% nuo Bolt komisinio ir net susigrąžinti pinigų metų pabaigoje.',
    type: 'article',
    datePublished: '2026-01-08',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Atvirkštinis PVM: mokestis, kurį dauguma pavežėjų pamiršta
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">PVM</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">5 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Kas yra atvirkštinis PVM ir kodėl kas mėnesį VMI siunčia baudas pavežėjams, kurie pamiršo jį deklaruoti?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Šis 5 min. gidas paaiškina, kaip apskaičiuoti, deklaruoti ir net susigrąžinti pinigų metų pabaigoje. Taip pat sužinosite apie <strong>Bolt mokesčius</strong> — ką reikia mokėti ir kaip tai daryti teisingai.
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
            Bolt mokesčiai: ką reikia žinoti?
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm mb-2">
            Dirbant per Bolt platformą reikia mokėti kelis mokesčius: <strong>GPM</strong> (5-20% nuo pelno), <strong>VSD</strong> (12,52%), <strong>PSD</strong> (6,98%) ir <strong>atvirkštinį PVM</strong> (21% nuo komisinio).
          </MotionText>
          <MotionText as="p" className="text-blue-700 text-xs">
            Šis straipsnis sutelktas į atvirkštinį PVM — dažniausiai pamirštamą Bolt mokestį. Apie kitus mokesčius skaitykite <Link to="/blog/pavezejo-mokesciai-gpm-psd-vsd" className="underline">čia</Link>.
          </MotionText>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kas yra atvirkštinis PVM?
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Kai perkate paslaugas iš ES šalies
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-2">
            „Bolt“ ir „Uber“ įmonės yra registruotos Estijoje ir Nyderlanduose. Kai jos teikia jums paslaugą (už kurią taikomas komisinis mokestis), jūs patys turite deklaruoti ir sumokėti PVM Lietuvoje vietoj jų.
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            Tai ir yra <strong>atvirkštinis</strong> PVM – PVM moka ne pardavėjas, o pirkėjas.
          </MotionText>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kaip skaičiuoti?
        </MotionText>

        <MotionDiv className="p-4 bg-white border border-zinc-250 rounded-xl mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm mb-2">
            PVM tarifas Lietuvoje – <strong>21 %</strong>
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-2">
              Skaičiavimo pavyzdys:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              „Bolt“ komisinis mokestis: 500 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              PVM (21 %): 500 × 0,21 = 105 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2 font-medium">
              Reikia sumokėti VMI: 105 €
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Deklaravimo procesas
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">
                  Atsisiųskite komisinio SF iš „Bolt" / „Uber" portalo
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">
                  Užpildykite PVM101 deklaraciją VMI portale
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">
                  Sumokėkite PVM iki kito mėnesio 25 dienos
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  4
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">
                  Įtraukite į išlaidas pildydami metinę GPM deklaraciją
                </MotionText>
              </div>
            </div>
          </div>

          <div className="mx-5 border-t border-zinc-250" />

          <div className="p-5">
            <div className="flex gap-3">
              <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <MotionText as="p" className="text-green-700 text-sm font-medium mb-1">
                  Gera žinia
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Nors turite mokėti šį PVM kas mėnesį, metų pabaigoje galite jį įtraukti į savo išlaidas ir taip sumažinti GPM.
                </MotionText>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          PVM mokėtojo riba 2026: kada privalote registruotis?
        </MotionText>

        <MotionDiv className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-5 h-5 text-red-700 mt-0.5" />
            <div>
              <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                Privaloma registracija nuo 2025 m. gegužės 1 d.
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm mb-3">
                <strong>Visi pavežėjai, naudojantys Bolt, Uber, Wolt, PRIVALO registruotis PVM mokėtojais</strong> — nėra jokios pajamų ribos, kuri leistų to nedaryti. Registruotis reikia per <strong>3 darbo dienas</strong> nuo paslaugos įsigijimo.
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm font-medium mb-2">
                Kokią schemą pasirinkti?
              </MotionText>
              <ul className="space-y-2 text-red-700 text-xs">
                <li className="flex gap-2">
                  <span className="text-red-600">▸</span>
                  <span><strong>SVS (Smulkiojo verslo schema)</strong> — pajamos iki 45 000 €/metus. Deklaruojate tik atvirkštinį PVM (PVM101), nereikia išrašinėti PVM sąskaitų klientams.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-red-600">▸</span>
                  <span><strong>Pilnas PVM mokėtojas</strong> — pajamos virš 45 000 €/metus. Deklaruojate FR0600, privalote išrašinėti PVM sąskaitas.</span>
                </li>
              </ul>
              <MotionText as="p" className="text-red-600 text-xs mt-3 italic">
                45 000 € riba lemia tik schemą — registruotis privaloma abiem atvejais.
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Praktiški pavyzdžiai
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden">
          <div className="p-4">
            <MotionText as="h3" className="text-zinc-900 text-sm font-medium mb-3">
              Pavyzdys 1: Vieno mėnesio apskaita
            </MotionText>
            <MotionDiv className="space-y-1 text-sm">
              <MotionText as="p" className="text-zinc-700 font-mono">
                Pajamos iš „Bolt“: 2000 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                „Bolt“ komisinis: 500 € (25 %)
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono border-t border-zinc-250 pt-2 mt-2">
                Atvirkštinis PVM: 105 €
              </MotionText>
              <MotionText as="p" className="text-zinc-600 text-xs mt-3">
                → Mokate VMI 105 € iki kito mėnesio 25 d.
              </MotionText>
            </MotionDiv>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="h3" className="text-zinc-900 text-sm font-medium mb-3">
              Pavyzdys 2: Metinė nauda (2026 m.)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs mb-3">
              Jei naudojate faktinių išlaidų metodą, komisinis ir sumokėtas PVM mažina apmokestinamąjį pelną:
            </MotionText>
            <MotionDiv className="space-y-1 text-sm">
              <MotionText as="p" className="text-zinc-700 font-mono">
                Metinės pajamos: 24 000 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                Be komisinio ir PVM → pelnas: 16 800 € (su 30% norma)
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                Su komisiniu + PVM → pelnas: 9 540 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono border-t border-zinc-250 pt-2 mt-2">
                Pelnas mažėja: −7 260 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                GPM (5%): −363 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                VSD (12,52% nuo 90%): −818 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 font-mono">
                PSD (6,98% nuo 90%): −456 €
              </MotionText>
              <MotionText as="p" className="text-green-700 text-sm font-mono font-medium border-t border-zinc-250 pt-2 mt-2">
                Mokesčių sutaupymas: 1 637 €/metus
              </MotionText>
              <MotionText as="p" className="text-zinc-500 text-xs mt-2">
                2026 m. tarifai. GPM 5% — efektyvus tarifas pelnui iki 20 000 € (20% bazinis − 15% kreditas)
              </MotionText>
            </MotionDiv>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Dažniausios klaidos
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  ❌ Pamirštama deklaruoti ir mokėti kas mėnesį
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Neįtraukiama į išlaidas metinėje deklaracijoje
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Neskaičiuojamas PVM nuo viso komisinio
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Pavėluojama sumokėti iki 25 dienos
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'pvm-pakeitimai-pavezejams-2025', title: 'PVM pakeitimai nuo gegužės 1 d.' },
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2026' },
          { slug: 'bolt-apskaita-dokumentai', title: 'Bolt apskaita: 3 dokumentai, kuriuos PRIVALOTE atsisiųsti' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default CommissionVatPage
